import type { Coupon, CouponTarget } from "@nlc/shared-types";
import type { Key } from "react-aria-components";

import { useState } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Label } from "@/components/field";
import { Modal } from "@/components/modal";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectListItemDescription,
  SelectListItemLabel,
  SelectPopover,
} from "@/components/select";

import { BillingNotice } from "../../components/BillingNotice";
import { useAddAccountCoupon, useCoupons } from "../hooks/useAccountCoupons";

const COUPON_TARGET_OPTIONS: { id: CouponTarget; label: string }[] = [
  { id: "subscription", label: "Whole subscription" },
  { id: "plan", label: "Plan only" },
  { id: "add-on", label: "Add-on only" },
];

interface AddCouponModalProps {
  readonly onClose: () => void;
  readonly projectId: number;
}

export function AddCouponModal({ onClose, projectId }: AddCouponModalProps) {
  const { data: coupons = [] } = useCoupons();
  const addCoupon = useAddAccountCoupon(projectId);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [target, setTarget] = useState<CouponTarget | null>(null);

  const handleSelectionChange = (key: Key | null) =>
    setSelectedCoupon(coupons.find((coupon) => coupon.id === key) ?? null);

  const submit = () => {
    if (!selectedCoupon || !target) return;

    addCoupon.mutate(
      { coupon: selectedCoupon, target },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      classNames={{ modal: "flex h-full w-full flex-col sm:h-auto sm:w-144" }}
      isDismissable
      isOpen
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <Dialog className="h-full">
        <DialogCloseButton />
        <DialogHeader className="px-xl pt-2xl text-lg font-semibold sm:border-b sm:border-border-secondary sm:px-3xl sm:pb-2xl sm:pt-3xl">
          Add coupon
        </DialogHeader>

        <DialogBody className="gap-xl px-xl pt-xl sm:px-3xl sm:py-2xl">
          <Select
            onSelectionChange={handleSelectionChange}
            placeholder="Select a coupon"
            selectedKey={selectedCoupon?.id ?? null}
          >
            <Label requiredHint>Choose a coupon</Label>
            <SelectButton />
            <SelectPopover>
              <SelectListBox items={coupons}>
                {(coupon) => (
                  <SelectListItem id={coupon.id} textValue={coupon.code}>
                    <SelectListItemLabel>{coupon.code}</SelectListItemLabel>
                    <SelectListItemDescription>
                      {coupon.discountLabel} · {coupon.durationLabel}
                    </SelectListItemDescription>
                  </SelectListItem>
                )}
              </SelectListBox>
            </SelectPopover>
          </Select>

          <Select
            onSelectionChange={(key) => setTarget(key as CouponTarget)}
            placeholder="Select..."
            selectedKey={target}
          >
            <Label requiredHint>Where will the coupon be applied?</Label>
            <SelectButton />
            <SelectPopover>
              <SelectListBox items={COUPON_TARGET_OPTIONS}>
                {(option) => (
                  <SelectListItem id={option.id}>{option.label}</SelectListItem>
                )}
              </SelectListBox>
            </SelectPopover>
          </Select>

          <BillingNotice description="This coupon will apply starting on the account's next invoice." />
        </DialogBody>

        <DialogFooter className="px-xl pb-xl sm:border-t sm:border-border-secondary sm:px-3xl sm:pb-3xl">
          <Button
            className="sm:flex-1"
            color="gray"
            onPress={onClose}
            size="lg"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="sm:flex-1"
            isDisabled={!selectedCoupon || !target || addCoupon.isPending}
            isLoading={addCoupon.isPending}
            onPress={submit}
            size="lg"
          >
            Add coupon
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
